import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { 
  Bot, 
  Sparkles, 
  ArrowRight, 
  ArrowLeft,
  Music,
  Gamepad2,
  BookOpen,
  Palette,
  Film,
  Trophy,
  Users,
  User,
  Globe,
  Code,
  Camera,
  Heart,
  Leaf,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const GRADES = [
  { value: "4", label: "4° Grado" },
  { value: "5", label: "5° Grado" },
  { value: "6", label: "6° Grado" },
  { value: "7", label: "7° Grado" },
  { value: "8", label: "8° Grado" },
  { value: "9", label: "9° Grado" },
  { value: "10", label: "1° Bachillerato" },
  { value: "11", label: "2° Bachillerato" },
];

const INTERESTS = [
  { id: "music", label: "Música", icon: Music },
  { id: "games", label: "Videojuegos", icon: Gamepad2 },
  { id: "reading", label: "Lectura", icon: BookOpen },
  { id: "art", label: "Arte y Dibujo", icon: Palette },
  { id: "movies", label: "Películas/Series", icon: Film },
  { id: "sports", label: "Deportes", icon: Trophy },
  { id: "social", label: "Redes Sociales", icon: Users },
  { id: "travel", label: "Viajar", icon: Globe },
  { id: "tech", label: "Tecnología", icon: Code },
  { id: "photography", label: "Fotografía", icon: Camera },
  { id: "animals", label: "Animales", icon: Heart },
  { id: "nature", label: "Naturaleza", icon: Leaf },
];

const Onboarding = () => {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Datos del estudiante (los datos del responsable y consentimiento se capturan en /comprar)
  const [studentData, setStudentData] = useState({
    firstName: "",
    lastName: "",
    age: "",
    grade: "",
    gender: "",
    interests: [],
  });

  const totalSteps = 3;

  const handleStudentChange = (field, value) => {
    setStudentData(prev => ({ ...prev, [field]: value }));
  };

  const toggleInterest = (interestId) => {
    setStudentData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(i => i !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/onboarding/complete`,
        {
          // Datos del estudiante
          first_name: studentData.firstName,
          last_name: studentData.lastName,
          age: parseInt(studentData.age),
          grade: studentData.grade,
          gender: studentData.gender,
          interests: studentData.interests,
          hobbies: [],
          learning_style: null,
          // Consentimiento parental se capturó en la compra B2C (/comprar)
          // o por flujo administrativo; aquí no se solicita de nuevo.
        },
        { withCredentials: true }
      );
      
      setUser(response.data);
      const welcomeMsg = studentData.gender === "femenino" ? "¡Bienvenida a QHUB.BUDDY!" : "¡Bienvenido a QHUB.BUDDY!";
      toast.success(welcomeMsg, {
        description: "+50 puntos por completar tu perfil"
      });
      navigate("/welcome");
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Error al guardar el perfil");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: // Datos básicos del estudiante
        return studentData.firstName && studentData.lastName && studentData.gender;
      case 2: // Edad y grado
        return studentData.age && studentData.grade;
      case 3: // Intereses
        return studentData.interests.length >= 2;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (canProceed() && step < totalSteps) {
      setStep(step + 1);
    } else if (step === totalSteps) {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col" data-testid="onboarding-page">
      {/* Header */}
      <header className="p-4 border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/qhub-buddy-logo.png" alt="QHUB.BUDDY" className="h-10 w-auto" />
            <div className="hidden sm:block">
              <p className="text-xs text-gray-500">Registro de estudiante</p>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all ${
                  s === step 
                    ? "w-8 bg-[#419cca]" 
                    : s < step 
                    ? "w-2 bg-[#419cca]" 
                    : "w-2 bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-4 py-6">
        <div className="w-full max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            
            {/* Step 1: Datos básicos del estudiante */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl shadow-sm border p-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Datos del Estudiante</h2>
                    <p className="text-sm text-gray-500">Información básica</p>
                  </div>
                </div>

                <div className="mb-6 p-3 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-sm text-green-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span><strong>Nota:</strong> Esta sección debe ser completada por el/la estudiante (hijo/a o menor a su cargo).</span>
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Nombre *</Label>
                      <Input
                        value={studentData.firstName}
                        onChange={(e) => handleStudentChange("firstName", e.target.value)}
                        placeholder="Nombre del estudiante"
                        className="mt-1"
                        data-testid="student-firstname"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Apellido *</Label>
                      <Input
                        value={studentData.lastName}
                        onChange={(e) => handleStudentChange("lastName", e.target.value)}
                        placeholder="Apellido del estudiante"
                        className="mt-1"
                        data-testid="student-lastname"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">Género *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: "masculino", label: "Masculino", emoji: "👦" },
                        { id: "femenino", label: "Femenino", emoji: "👧" },
                      ].map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => handleStudentChange("gender", g.id)}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            studentData.gender === g.id
                              ? "border-green-500 bg-green-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`gender-${g.id}`}
                        >
                          <span className="text-2xl mb-2 block">{g.emoji}</span>
                          <span className="font-medium text-gray-800">{g.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Edad y Grado */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl shadow-sm border p-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Nivel Educativo</h2>
                    <p className="text-sm text-gray-500">Edad y grado actual</p>
                  </div>
                </div>

                <div className="mb-6 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span><strong>Para el estudiante:</strong> Selecciona tu edad y grado actual.</span>
                  </p>
                </div>

                <div className="space-y-6">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">¿Cuántos años tienes? *</Label>
                    <div className="flex flex-wrap gap-2">
                      {[10, 11, 12, 13, 14, 15, 16, 17, 18].map((age) => (
                        <button
                          key={age}
                          type="button"
                          onClick={() => handleStudentChange("age", age.toString())}
                          className={`w-12 h-12 rounded-xl border-2 font-bold transition-all ${
                            studentData.age === age.toString()
                              ? "border-amber-500 bg-amber-50 text-amber-700"
                              : "border-gray-200 hover:border-gray-300 text-gray-600"
                          }`}
                          data-testid={`age-${age}`}
                        >
                          {age}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">¿En qué grado estás? *</Label>
                    <div className="grid grid-cols-1 gap-2">
                      {GRADES.map((grade) => (
                        <button
                          key={grade.value}
                          type="button"
                          onClick={() => handleStudentChange("grade", grade.value)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            studentData.grade === grade.value
                              ? "border-amber-500 bg-amber-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`grade-${grade.value}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-800">{grade.label}</span>
                            {studentData.grade === grade.value && (
                              <Check className="w-5 h-5 text-amber-600" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Intereses */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white rounded-2xl shadow-sm border p-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Tus Intereses</h2>
                    <p className="text-sm text-gray-500">Personaliza tu experiencia</p>
                  </div>
                </div>

                <div className="mb-6 p-3 bg-pink-50 rounded-lg border border-pink-100">
                  <p className="text-sm text-pink-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span><strong>Para {studentData.firstName || "el estudiante"}:</strong> Selecciona al menos 2 cosas que te gusten. Esto ayudará a tu tutor a conocerte mejor.</span>
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {INTERESTS.map((interest) => {
                    const Icon = interest.icon;
                    const isSelected = studentData.interests.includes(interest.id);
                    return (
                      <button
                        key={interest.id}
                        type="button"
                        onClick={() => toggleInterest(interest.id)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? "border-pink-500 bg-pink-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid={`interest-${interest.id}`}
                      >
                        <Icon className={`w-6 h-6 mx-auto mb-2 ${
                          isSelected ? "text-pink-600" : "text-gray-400"
                        }`} />
                        <span className={`text-xs font-medium ${
                          isSelected ? "text-pink-700" : "text-gray-600"
                        }`}>
                          {interest.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <p className="mt-4 text-sm text-gray-500 text-center">
                  Seleccionados: {studentData.interests.length}/2 mínimo
                </p>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="border-t bg-white p-4 sticky bottom-0">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={prevStep}
            disabled={step === 1}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Anterior
          </Button>

          <div className="text-sm text-gray-500">
            Paso {step} de {totalSteps}
          </div>

          <Button
            onClick={nextStep}
            disabled={!canProceed() || loading}
            className="gap-2 bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:opacity-90"
            data-testid="next-btn"
          >
            {loading ? (
              "Guardando..."
            ) : step === totalSteps ? (
              <>
                Completar
                <Check className="w-4 h-4" />
              </>
            ) : (
              <>
                Siguiente
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default Onboarding;
